import React, { useRef, useState } from "react";

const PUBLIC_BASE = "https://wolfdogvillage.com/learn/articles/";
const focusRing = "rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background";
const control = `inline-flex items-center px-3 py-2 text-sm font-semibold border border-border rounded-md hover:border-foreground/40 ${focusRing}`;

export const ShareArticle = ({ title, slug, position }) => {
  const url = PUBLIC_BASE + slug;
  const id = `share-${position}`;
  const [status, setStatus] = useState("");
  const [fallback, setFallback] = useState(false);
  const fieldRef = useRef(null);
  const copyRef = useRef(null);
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const openFallback = () => {
    setStatus("");
    setFallback(true);
    setTimeout(() => { fieldRef.current?.focus(); fieldRef.current?.select(); }, 0);
  };

  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("unavailable");
      await navigator.clipboard.writeText(url);
      setFallback(false);
      setStatus("Link copied.");
    } catch {
      openFallback();
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title, url });
    } catch (e) {
      if (e?.name !== "AbortError") openFallback();
    }
  };

  const closeFallback = () => {
    setFallback(false);
    copyRef.current?.focus();
  };

  return (
    <section aria-labelledby={`${id}-heading`} data-testid={id} className="mt-8">
      <h2 id={`${id}-heading`} className="eyebrow">
        Share Article<span className="sr-only">{position === "top" ? " (top)" : " (bottom)"}</span>
      </h2>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Share “${title}” on Facebook (opens in a new tab)`}
          data-testid={`${id}-facebook`}
          className={control}
        >
          Facebook
        </a>
        <button ref={copyRef} type="button" onClick={copy} data-testid={`${id}-copy`} className={control}>
          Copy Link
        </button>
        {canShare && (
          <button type="button" onClick={nativeShare} data-testid={`${id}-native`} className={control}>
            Share…
          </button>
        )}
      </div>
      <p role="status" aria-live="polite" data-testid={`${id}-status`} className="mt-2 text-sm text-muted-foreground">{status}</p>
      {fallback && (
        <div className="mt-2" data-testid={`${id}-fallback`}>
          <label htmlFor={`${id}-url`} className="block text-sm font-semibold">Article link</label>
          <p id={`${id}-hint`} className="text-sm text-muted-foreground">Copying was not available. Select the link below and copy it manually.</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input
              ref={fieldRef}
              id={`${id}-url`}
              type="text"
              readOnly
              value={url}
              aria-describedby={`${id}-hint`}
              onFocus={(e) => e.target.select()}
              data-testid={`${id}-url`}
              className="w-full sm:w-auto sm:flex-1 min-w-0 px-3 py-2 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button type="button" onClick={closeFallback} data-testid={`${id}-fallback-close`} className={control}>
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
